import { useHookstate } from "@hookstate/core";
import { memo, useCallback } from "react";
import { AudienceIcon } from "../../../../components/icons/AudienceIcon.tsx";
import { ClockIcon } from "../../../../components/icons/ClockIcon.tsx";
import { GenreIcon } from "../../../../components/icons/GenreIcon.tsx";
import { PenIcon } from "../../../../components/icons/PenIcon.tsx";
import { InitResponsePayloadResults } from "../../../../services/gc/types/index.ts";
import { ComicCrowdfundingTypeEntity, BrowseTabType } from "../../../../types.ts";
import { ArtistTypeFilter, ArtistTypeFilterTitle } from "./ArtistTypeFilter";
import { BrowseFiltersCarousel } from "./BrowseFilterCarousel";
import { BrowseFiltersAccordion } from "./BrowseFiltersAccordion";
import {
  ComicCategoryFilterTitle,
  ComicCategoryTypeFilter,
} from "./ComicCategoryFilter";
import { ComicFormatFilter, ComicFormatFilterTitle } from "./ComicFormatFilter";
import { ComicTypeFilter, ComicTypeFilterTitle } from "./ComicTypeFilter";
import {
  CountryFacetGroupFilter,
  CountryFacetGroupFilterTitle,
} from "./CountryFacetGroupFilter";
import {
  CrowdfundingTypeFilter,
  CrowdfundingTypeFilterTitle,
} from "./CrowdfundingFilter.tsx";
import {
  LanguageFacetGroupFilter,
  LanguageFacetGroupFilterTitle,
} from "./LanguageFacetGroupFilter";
import { LicenseTypeFilter, LicenseTypeFilterTitle } from "./LicenseTypeFilter";
import { useComicFacetGroupFilter } from "../../hooks/useComicFacetGroupFilter.tsx";
import { appStore } from "../../../../hookstate/appStore.ts";
import { FilterState } from "../../utils/browseUrlParser.ts";

interface BrowseFiltersProps {
  frozen?: boolean;
  isModal?: boolean;
  invalidate?: () => void;
  tab?: BrowseTabType;
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
}
export const BrowseFilters = memo(
  ({
    isModal = false,
    invalidate,
    tab = BrowseTabType.comics,
    filters,
    onFilterChange,
  }: BrowseFiltersProps) => {
    const state = useHookstate(appStore);
    const {
      artist_types: artistTypes,
      comic_crowdfunding_types: crowdfundingTypes,
      comic_facet_groups: comicFacetGroups,
      comic_formats: comicFormats,
      comic_types: comicTypes,
      comic_genres: comicGenres,
      countries: countries,
      languages: languages,
      license_types: licenseTypes,
    } = state.init.get() as InitResponsePayloadResults & {
      comic_crowdfunding_types: ComicCrowdfundingTypeEntity[];
    };

    const handleFacetGroupChange = useCallback(
      (categoryId: number, nextValue: string[]) => {
        onFilterChange({
          comicFacets: { ...filters.comicFacets, [`${categoryId}`]: nextValue },
        });
      },
      [filters.comicFacets, onFilterChange],
    );

    const audienceFacetFilter = useComicFacetGroupFilter(
      filters.comicFacets,
      isModal,
      comicFacetGroups
        ? comicFacetGroups.filter((facet) => facet.id === 1)
        : [],
      handleFacetGroupChange,
      <AudienceIcon />,
    );
    const themeFacetFilter = useComicFacetGroupFilter(
      filters.comicFacets,
      isModal,
      comicFacetGroups
        ? comicFacetGroups.filter((facet) => facet.id === 2)
        : [],
      handleFacetGroupChange,
      <GenreIcon />,
    );
    const artStyleFacetFilter = useComicFacetGroupFilter(
      filters.comicFacets,
      isModal,
      comicFacetGroups
        ? comicFacetGroups.filter((facet) => facet.id === 5)
        : [],
      handleFacetGroupChange,
      <PenIcon />,
    );
    const timeEraStyleFacetFilter = useComicFacetGroupFilter(
      filters.comicFacets,
      isModal,
      comicFacetGroups
        ? comicFacetGroups.filter((facet) => facet.id === 3)
        : [],
      handleFacetGroupChange,
      <ClockIcon />,
    );

    const makeAdapter = (
      value: any,
      key: keyof FilterState,
    ) => ({
      value,
      onChange: (nextValue: any) => onFilterChange({ [key]: nextValue }),
    });

    const filtersList = [
      licenseTypes
        ? [
            <LicenseTypeFilterTitle
              isTrigger={!isModal}
              licenseTypes={licenseTypes}
              value={filters.licenseType}
            />,
            <LicenseTypeFilter
              value={filters.licenseType}
              licenseTypes={licenseTypes}
              tab={tab}
              onChange={(nextLicenseType) => {
                onFilterChange({ licenseType: nextLicenseType });
              }}
            />,
            makeAdapter(filters.licenseType, "licenseType"),
          ]
        : null,
      comicTypes
        ? [
            <ComicTypeFilterTitle
              isTrigger={!isModal}
              comicTypes={comicTypes}
              value={filters.comicEntityType}
            />,
            <ComicTypeFilter
              value={filters.comicEntityType}
              comicTypes={comicTypes}
              onChange={(nextType) => {
                onFilterChange({ comicEntityType: nextType });
              }}
            />,
            makeAdapter(filters.comicEntityType, "comicEntityType"),
          ]
        : null,
      comicGenres
        ? [
            <ComicCategoryFilterTitle
              isTrigger={!isModal}
              comicCategories={comicGenres}
              value={filters.comicCategory}
            />,
            <ComicCategoryTypeFilter
              value={filters.comicCategory}
              comicCategories={comicGenres}
              onChange={(nextCategory) => {
                onFilterChange({ comicCategory: nextCategory });
              }}
            />,
            makeAdapter(filters.comicCategory, "comicCategory"),
          ]
        : null,
      ...themeFacetFilter,
      artistTypes
        ? [
            <ArtistTypeFilterTitle
              isTrigger={!isModal}
              artistTypes={artistTypes}
              value={filters.artistEntityType}
            />,
            <ArtistTypeFilter
              value={filters.artistEntityType}
              artistTypes={artistTypes}
              onChange={(nextAristType) => {
                onFilterChange({ artistEntityType: nextAristType });
              }}
            />,
            makeAdapter(filters.artistEntityType, "artistEntityType"),
          ]
        : null,
      ...audienceFacetFilter,
      comicFormats
        ? [
            <ComicFormatFilterTitle
              isTrigger={!isModal}
              comicFormats={comicFormats}
              value={filters.comicFormat}
            />,
            <ComicFormatFilter
              value={filters.comicFormat}
              comicFormats={comicFormats}
              onChange={(nextComicFormat) => {
                onFilterChange({ comicFormat: nextComicFormat });
              }}
            />,
            makeAdapter(filters.comicFormat, "comicFormat"),
          ]
        : null,
      ...artStyleFacetFilter,
      crowdfundingTypes
        ? [
            <CrowdfundingTypeFilterTitle
              isTrigger={!isModal}
              crowdfundingTypes={crowdfundingTypes}
              value={filters.crowdfundingType}
            />,
            <CrowdfundingTypeFilter
              value={filters.crowdfundingType}
              crowdfundingTypes={crowdfundingTypes}
              onChange={(nextCrowdfundingType) => {
                onFilterChange({ crowdfundingType: nextCrowdfundingType });
              }}
            />,
            makeAdapter(filters.crowdfundingType, "crowdfundingType"),
          ]
        : null,
      countries
        ? [
            <CountryFacetGroupFilterTitle
              isTrigger={!isModal}
              countries={countries}
              activeValue={filters.countryFacets}
            />,
            <CountryFacetGroupFilter
              value={filters.countryFacets}
              countries={countries}
              onChange={(nextCountries) => {
                onFilterChange({ countryFacets: nextCountries });
              }}
            />,
            makeAdapter(filters.countryFacets, "countryFacets"),
          ]
        : null,
      languages
        ? [
            <LanguageFacetGroupFilterTitle
              isTrigger={!isModal}
              languages={languages}
              activeValue={filters.languageFacets}
            />,
            <LanguageFacetGroupFilter
              value={filters.languageFacets}
              languages={languages}
              onChange={(nextLanguages) => {
                onFilterChange({ languageFacets: nextLanguages });
              }}
            />,
            makeAdapter(filters.languageFacets, "languageFacets"),
          ]
        : null,
      ...timeEraStyleFacetFilter,
    ];

    if (isModal) {
      return <BrowseFiltersAccordion filters={filtersList} />;
    }
    return (
      <BrowseFiltersCarousel
        invalidate={() => {
          invalidate?.();
        }}
        filters={filtersList}
      />
    );
  },
);
